/**
 * useModules — React hook that returns the set of functional modules
 * enabled for the current user. Backed by GET /api/me/modules.
 *
 * Fetches fresh on each mount; lightweight enough (~1KB JSON) that
 * a per-component refetch is cheaper than the caching headaches we
 * hit when token state changes mid-session (login, logout,
 * impersonation, super-admin "view as org" preview).
 *
 * Super admins get every module (the backend returns
 * `is_super_admin: true` and the full id list). Regular users
 * receive their effective set; an unauth/error response falls
 * back to "all modules enabled" so the UI never bricks itself
 * if the modules service is briefly unreachable.
 */
import { useEffect, useState, useCallback } from 'react';
import authedFetch from './authedFetch';
import secureStorage from './secureStorage';
import logger from './logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Bumping this counter triggers every mounted useModules() to
// re-fetch its data. Used by ModulesTab after a successful PUT
// so the super admin sees their own toggles reflected without a
// hard refresh.
let invalidationCounter = 0;
const subscribers = new Set();

export function invalidateModulesCache() {
  invalidationCounter += 1;
  subscribers.forEach((cb) => cb());
}

export default function useModules() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setTick] = useState(0);

  useEffect(() => {
    const onInvalidate = () => setTick((t) => t + 1);
    subscribers.add(onInvalidate);
    return () => { subscribers.delete(onInvalidate); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const token = secureStorage.getItem('session_token');
    if (!token) {
      setData(null);
      setLoading(false);
      return undefined;
    }
    authedFetch(`${API_URL}/api/me/modules`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setLoading(false);
      })
      .catch((e) => {
        logger.warn('useModules fetch failed', e);
        if (cancelled) return;
        setData(null);
        setLoading(false);
      });
    return () => { cancelled = true; };
    // invalidationCounter is referenced so React re-runs the effect
    // when invalidateModulesCache() is called.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidationCounter]);

  const enabledSet = data?.enabled ? new Set(data.enabled) : null;

  const isEnabled = useCallback((moduleId) => {
    if (!enabledSet) return true; // permissive fallback while loading or on error
    if (data?.is_super_admin) return true;
    return enabledSet.has(moduleId);
  }, [enabledSet, data?.is_super_admin]);

  return {
    loading,
    data,
    enabledSet,
    isEnabled,
    isSuperAdmin: !!data?.is_super_admin,
  };
}
